/**
 * useUpdateMenu.tsx
 */
import Features from '@shared/types/enums/features';
import MenuData, { type MenuItem } from '@shared/components/menu/menuData';
import User from '@shared/classes/user';
import { appSelector } from '@shared/store/slices/app.slice';
import { selectRoute } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Hook that retriebes the updated menu data and state.
 * @returns {Object} The updated menu data and state.
 */
export default function useUpdateMenu() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Selectors
  const {
    status: { anonymous },
  } = useAppSelector(appSelector);
  const route = useAppSelector(selectRoute);
  const { initialized, operator, planId } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  // Returns filtered menu items based on the plan and operator.
  return useMemo(() => {
    const plan = User.getPlan(planId);
    const items: MenuItem[] = [];
    Object.values(MenuData).map((item) => {
      let addItem = true;
      switch (item) {
        case MenuData.Movies:
        case MenuData.Series:
        case MenuData.Shows:
          addItem = !anonymous
            ? plan?.features?.includes(Features.LAST_SHOWS) || false
            : true;
          break;
        case MenuData.Recordings:
          addItem =
            !(operator?.mySpaceEnabled ?? false) &&
            (!anonymous
              ? plan?.features?.includes(Features.ALLOW_RECORD) ?? false
              : true);
          break;
        case MenuData.MoreContent:
          addItem = operator?.moreContentEnabled ?? false;
          break;
        case MenuData.MySpace:
          addItem = operator?.mySpaceEnabled ?? false;
          break;
      }
      addItem && items.push(item);
    });
    return items;
  }, [anonymous, initialized, operator, planId, route, MenuData]);
}
